import {
  Identify,
  init,
  identify,
  logEvent,
  reset,
  setUserId,
} from "@amplitude/analytics-browser";
import { ANALYTICS_EVENTS, type AnalyticsEventName } from "./events";

type NullableRecord = Record<string, unknown>;

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;

const isBrowser = typeof window !== "undefined";

let initialized = false;
let analyticsEnabled = false;
let launchTracked = false;
const IDENTITY_EVENT = "__identify__";

let pendingEvents: Array<{
  name: AnalyticsEventName | string;
  properties?: NullableRecord;
}> = [];
let defaultEventProperties: NullableRecord = {};

const sanitize = (input?: NullableRecord) => {
  if (!input) {
    return undefined;
  }

  return Object.entries(input).reduce<NullableRecord>((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }

    acc[key] =
      typeof value === "number" && Number.isFinite(value) ? Number(value) : value;
    return acc;
  }, {});
};

const trackNow = (
  eventName: AnalyticsEventName | string,
  properties?: NullableRecord
) => {
  if (!initialized || !analyticsEnabled) {
    pendingEvents.push({ name: eventName, properties });
    return;
  }

  logEvent(eventName, {
    ...sanitize(defaultEventProperties),
    ...sanitize(properties),
  });
};

const flushPendingEvents = () => {
  if (!initialized || !analyticsEnabled || pendingEvents.length === 0) {
    return;
  }

  pendingEvents.forEach(({ name, properties }) => {
    if (name === IDENTITY_EVENT) {
      syncUserIdentity({
        userId: (properties?.userId as string | undefined) ?? null,
        properties,
      });
      return;
    }

    trackNow(name, properties);
  });
  pendingEvents = [];
};

const resolveDeviceContext = () => {
  if (!isBrowser) return {};

  const nav = window.navigator;
  const ua = nav.userAgent || "";
  const platform = nav.platform || "unknown";
  const language = nav.language || "unknown";
  const screenWidth = window.screen?.width;
  const screenHeight = window.screen?.height;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
  const osMatch =
    ua.match(
      /(Android\s[\d._]+)|(iPhone\sOS\s[\d_]+)|(iPad\sOS\s[\d_]+)|(Windows\sNT\s[\d._]+)|(Mac\sOS\sX\s[\d._]+)/i
    ) || [];
  const osVersion = osMatch[0]?.replace(/_/g, ".") ?? undefined;

  return sanitize({
    device_platform: platform,
    device_language: language,
    device_user_agent: ua,
    device_screen_width: screenWidth,
    device_screen_height: screenHeight,
    device_timezone: timezone,
    device_is_mobile: isMobile,
    device_os_version: osVersion,
  });
};

export const initializeAnalytics = (options?: {
  userId?: string;
  userProperties?: NullableRecord;
  disabled?: boolean;
}) => {
  if (!isBrowser || initialized || options?.disabled) {
    analyticsEnabled = !options?.disabled && analyticsEnabled;
    return initialized;
  }

  if (!AMPLITUDE_API_KEY) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[analytics] REACT_APP_AMPLITUDE_API_KEY is not defined.");
    }
    analyticsEnabled = false;
    return false;
  }

  init(AMPLITUDE_API_KEY, undefined, {
    defaultTracking: {
      sessions: true,
      pageViews: false,
      formInteractions: true,
      fileDownloads: true,
    },
  });
  initialized = true;
  analyticsEnabled = true;

  updateDefaultEventProperties({
    app_version:
      process.env.REACT_APP_VERSION ??
      process.env.REACT_APP_COMMIT_SHA ??
      process.env.REACT_APP_BRANCH ??
      "unknown",
    environment: process.env.NODE_ENV,
    ...resolveDeviceContext(),
  });

  if (options?.userId) {
    setUserId(options.userId);
  }

  if (options?.userProperties) {
    setUserProperties(options.userProperties);
  }

  if (!launchTracked) {
    trackNow(ANALYTICS_EVENTS.APP_LAUNCH);
    launchTracked = true;
  }

  flushPendingEvents();
  return true;
};

export const disableAnalytics = () => {
  analyticsEnabled = false;
};

export const trackAnalyticsEvent = (
  eventName: AnalyticsEventName | string,
  properties?: NullableRecord
) => {
  if (!analyticsEnabled) {
    return;
  }

  trackNow(eventName, properties);
};

export const updateDefaultEventProperties = (properties: NullableRecord) => {
  defaultEventProperties = {
    ...defaultEventProperties,
    ...sanitize(properties),
  };
};

type IdentifyValue = string | number | boolean | string[] | number[];

const toIdentifyValue = (value: unknown): IdentifyValue | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => {
        if (typeof item === "string" || typeof item === "number") {
          return String(item);
        }
        if (typeof item === "boolean") {
          return item ? "true" : "false";
        }
        try {
          return JSON.stringify(item);
        } catch {
          return undefined;
        }
      })
      .filter((item): item is string => typeof item === "string");

    return normalized.length ? normalized : undefined;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

export const setUserProperties = (properties: NullableRecord) => {
  if (!initialized || !analyticsEnabled) {
    return;
  }

  const identifyObj = new Identify();
  Object.entries(sanitize(properties) || {}).forEach(([key, value]) => {
    const normalized = toIdentifyValue(value);
    if (normalized !== undefined) {
      identifyObj.set(key, normalized);
    }
  });
  identify(identifyObj);
};

export const syncUserIdentity = (options: {
  userId?: string | null;
  properties?: NullableRecord;
}) => {
  if (!initialized || !analyticsEnabled) {
    if (options.userId) {
      pendingEvents.push({
        name: IDENTITY_EVENT,
        properties: { userId: options.userId, ...options.properties },
      });
    }
    return;
  }

  if (options.userId) {
    setUserId(options.userId);
    if (options.properties) {
      setUserProperties(options.properties);
    }
  }

  if (!options.userId) {
    reset();
  }
};

export const clearAnalyticsIdentity = () => {
  if (!initialized) return;
  reset();
};

export const isAnalyticsEnabled = () => analyticsEnabled && initialized;

